import { debug, ParamErrCode, UploadErrCode, DeleteNetErrCode, PutNetErrCode, HttpErrCode } from 'zf-web-infra';
import { t } from 'zf-web-components';
import type { ParseError, ParseResult } from './types';

/**
 * 提供解析异常的方法，返回 true 表示异常已经被处理
 */
export class ErrorParser {

  /**
   * 清理历史异常的定时器
   */
  private timeout: NodeJS.Timeout | null = null;
  /**
   * 登录页面地址
   */
  private static readonly LOGIN_URL = '/login';
  /**
   * 历史异常记录，用于抑制相同异常
   */
  private readonly history: Map<string, ParseError>;

  constructor() {
    this.history = new Map<string, ParseError>();
  }

  /**
   * 解析并处理异常
   * @param error
   * @returns 返回true时，跳转错误页面
   */
  parse(error: ParseError): ParseResult {
    debug.common('parse', error);

    if (this.isEzvizError(error)) {
      return { type: 'ignore', ...error };
    }

    // 抑制相同的异常
    if (this.isRestrict(error)) {
      return { ...error, type: 'ignore' };
    }

    // 401，跳转登录页面
    if (this.isUnauthorized(error)) {
      debug.common('Unauthorized');
      return { ...error, type: 'ignore' };
    }

    // 处理超时异常，跳转登录页面
    if (this.isLoginTimeout(error)) {
      debug.common('login timeout');
      return { ...error, type: 'ignore' };
    }

    // 表单异常，忽略
    if (this.isFormError(error)) {
      debug.common('form error');
      return { ...error, type: 'ignore' };
    }

    // 取消操作产生的异常忽略（如：OSS主动取消上传）
    if (this.isCancelError(error)) {
      return { ...error, type: 'ignore' };
    }

    // 处理删除响应不正确异常（如，删除的返回结果为0）
    if (this.isDeleteResponseError(error)) {
      debug.common('delete response error');
      return { ...error, type: 'alert', formatted: t('删除异常') };
    }

    // 处理多次请求POST请求异常
    if (this.isUpdateResponseError(error)) {
      debug.common('update response error');
      return { ...error, type: 'alert', formatted: t('更新异常') };
    }

    // 导入错误
    if (this.isImportError(error)) {
      debug.common('import error');
      return { ...error, type: 'confirm', formatted: error.message.split(',') };
    }

    // 密码错误
    if (this.isPasswordError(error)) {
      debug.common('password error');
      return { ...error, type: 'alert', formatted: t(error.message) };
    }

    // 请求超时 408
    if (this.parseRequestTimeout(error)) {
      debug.common('request timeout error');
      return { ...error, type: 'alert', formatted: t('请求超时') };
    }

    // 处理后台接口存在校验规则的错误
    if (this.parseValidatorError(error)) {
      debug.common('request error');
      return { ...error, type: 'alert', formatted: t(error.message) };
    }

    // 处理400系列错误，跳转错误页面
    if (this.parseRequestError(error)) {
      debug.common('request error');
      return { ...error, type: 'show' };
    }

    // 处理500系列错误，跳转错误页面
    if (this.parseResponseError(error)) {
      debug.common('response error');
      return { ...error, type: 'show' };
    }

    debug.common('unknown error');
    return { ...error, type: 'alert', formatted: t(error.message) };
  }

  // TODO: 视频监控暂时规避报错提示，需优化
  private isEzvizError(error: ParseError): boolean {
    const msg = error?.message ?? '';
    const ezvizKeywords = ['ezuikit', 'startPlay', 'recCount'];

    if (ezvizKeywords.some(keyword => msg.includes(keyword))) {
      return true;
    }

    return document.getElementById('monitor-ezviz') !== null;
  }

  private parseValidatorError(error: ParseError): boolean {
    if (typeof error.code !== 'number') {
      return false;
    }
    if (error.code === HttpErrCode.BAD_REQUEST && error?.error.code === HttpErrCode.BAD_REQUEST) {
      return true;
    }
    return false;
  }

  private parseRequestTimeout(error: ParseError): boolean {
    return error.code === 408;
  }

  private parseRequestError(error: ParseError): boolean {
    if (typeof error.code !== 'number') {
      return false;
    }
    if (error.code >= 400 && error.code < 500) {
      return true;
    }
    return false;
  }

  private parseResponseError(error: ParseError): boolean {
    if (typeof error.code === 'number' && error.code > 500) {
      return true;
    }
    return false;
  }

  private isPasswordError(error: ParseError): boolean {
    return error.code === ParamErrCode.PASSWORD;
  }

  private isImportError(error: ParseError): boolean {
    return error.code === UploadErrCode.UPLOAD_ERROR;
  }

  private isDeleteResponseError(error: ParseError): boolean {
    return error.code === DeleteNetErrCode.DELETE_NET_ERROR;
  }

  private isUpdateResponseError(error: ParseError): boolean {
    return error.code === PutNetErrCode.PUT_NET_ERROR;
  }

  private isLoginTimeout(error: ParseError): boolean {
    if (error.code === ParamErrCode.COMMON) {
      return false;
    }

    if (error.message !== 'NOT LOGGED IN') {
      return false;
    }

    setTimeout(() => window.location.href = '/login', 100);
    return true;
  }

  private isUnauthorized(error: ParseError): boolean {
    if (error.code !== HttpErrCode.UNAUTHORIZED) {
      return false;
    }
    const loginUrl = ErrorParser.LOGIN_URL;

    if (window.location.pathname === loginUrl || window.location.pathname === '/') {
      return true;
    }

    // todo 刷新token
    setTimeout(() => window.location.href = `${loginUrl}?redirect=${window.location.href}`, 100);
    return true;
  }

  /**
   * 表单输入异常
   * TODO: 应该换更明确的异常判断方法 reason.errorFields 有数据属于 antd 表单错误不需alert
   * @param error
   * @returns
   */
  private isFormError({ error }: ParseError): boolean {
    if (error.errorFields == null) {
      return false;
    }
    return true;
  }

  /**
   * 取消操作产生的异常忽略（如：OSS主动取消上传）
   * @param error
   * @returns
   */
  private isCancelError(error: ParseError): boolean {
    if (error.error.name === 'cancel') {
      return true;
    }
    return false;
  }

  /**
   * 抑制相同的异常
   * @param error
   * @param timer
   * @returns
   */
  private isRestrict(error: ParseError, timer = 1000): boolean {
    const { timestamp, kind } = error;
    const key = `${Math.floor(timestamp / 100)}-${kind}`;

    if (this.history.has(key)) {
      debug.common('restrict', key);

      if (this.timeout != null) {
        clearTimeout(this.timeout);
      }
      this.timeout = setTimeout(() => this.history.clear(), timer);
      return true;
    }

    this.history.set(key, error);
    return false;
  }

}
